'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { parseAuthIdentifier, IDENTIFIER_HELP } from '@/lib/auth-identifier';
import {
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
  EnvelopeIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';

export default function LoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [identifierTouched, setIdentifierTouched] = useState(false);
  const validIdentifier = Boolean(parseAuthIdentifier(identifier));
  const identifierError = validIdentifier || (!identifierTouched && !identifier)
    ? ''
    : !identifier.trim()
      ? 'Enter your email or mobile number.'
      : identifier.includes('@')
        ? 'Enter a valid email address, such as you@example.com.'
        : /^[+0-9 ()-]+$/.test(identifier.trim())
          ? 'Enter a valid 10-digit Indian mobile number or an international mobile number with +country code.'
          : 'Enter a valid email address or mobile number.';

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!parseAuthIdentifier(identifier)) {
      setError(IDENTIFIER_HELP);
      return;
    }
    setLoading(true);
    try {
      await signIn(identifier, password);
      router.push('/');
      router.refresh();
    } catch (err: any) {
      setError(err?.message || 'Invalid email, mobile number, or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Background blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="blob-primary absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-40" />
        <div className="blob-green absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-30" />
      </div>

      <div className="relative w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl gradient-blue flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/20">
            <UserCircleIcon style={{ width: 32, height: 32 }} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Welcome Back</h1>
          <p className="text-muted-foreground text-sm mt-1">Sign in to your BizzBox account</p>
        </div>

        {/* Login Card */}
        <div className="bg-card border border-border rounded-2xl p-6 shadow-xl">
          <form onSubmit={handleLogin} className="space-y-4">
            {/* Email or mobile */}
            <div>
              <label htmlFor="auth-identifier" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                Email or mobile number
              </label>
              <div className="relative">
                <EnvelopeIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  style={{ width: 16, height: 16 }}
                />
                <input
                  type="text"
                  id="auth-identifier"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  aria-describedby={identifierError ? 'identifier-error identifier-help' : 'identifier-help'}
                  aria-invalid={Boolean(identifierError)}
                  maxLength={254}
                  onBlur={() => setIdentifierTouched(true)}
                  value={identifier}
                  onChange={(e) => { setIdentifier(e.target.value); setError(''); }}
                  placeholder="Email or mobile number"
                  required
                  className={`w-full bg-input border rounded-xl pl-10 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 transition-all ${identifierError ? 'border-red-500 focus:ring-red-500/50' : 'border-border focus:ring-primary/50 focus:border-primary/50'}`}
                />
              </div>
            </div>

            {identifierError && <p id="identifier-error" role="status" className="text-xs text-red-500">{identifierError}</p>}
            <p id="identifier-help" className="text-xs text-muted-foreground">{IDENTIFIER_HELP}</p>

            {/* Password */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                Password
              </label>
              <div className="relative">
                <LockClosedIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  style={{ width: 16, height: 16 }}
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full bg-input border border-border rounded-xl pl-10 pr-10 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? (
                    <EyeSlashIcon style={{ width: 16, height: 16 }} />
                  ) : (
                    <EyeIcon style={{ width: 16, height: 16 }} />
                  )}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-xs text-red-400">{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !validIdentifier}
              className="w-full py-3.5 rounded-xl gradient-blue text-white font-bold text-sm flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-60"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-5 pt-4 border-t border-border text-center space-y-2">
            <p className="text-xs text-muted-foreground">
              Don&apos;t have an account?{' '}
              <Link href="/signup" className="text-primary font-semibold hover:underline">
                Sign Up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
